import {API_URL} from '../../../config/constants'
import {getTestDriveOrderSummaryResponse, testDriveApi} from '../../../api/test_drive'

export let scenarioDetails: TestDriveScenarioDetails | undefined
export let demoBankCommissionsCatalog: TestDriveBankCommissions | undefined

export type DemoExtraService = Omit<ExtraService, 'cost_total' | 'currency' | 'services'> & {
  cost_total: number
  currency: string
  services: ExtraServiceItem[]
}

export let demoExtraServicesCatalog: DemoExtraService[] = []

const DEMO_CONTAINER_COUNTS = {
  type_20dc: 0,
  type_20dh: 0,
  type_40dc: 0,
  type_40hc: 0,
}

export const normalizeDemoHsCode = (code?: string) => code?.replace(/\s/g, '') || ''

const getDemoProductByHsCode = (code?: string) => {
  const normalizedCode = normalizeDemoHsCode(code)

  return scenarioDetails?.order.invoices
    .flatMap(invoice => invoice.products || [])
    .find(product => normalizeDemoHsCode(product.code) === normalizedCode)
}

export const getDemoSuggestAdditionalFields = (code?: string) => (
  getDemoProductByHsCode(code)?.suggest_additional_fields || {}
)

export const getDemoExciseOptions = (code?: string) => (
  getDemoProductByHsCode(code)?.excise_options
)

const resetDemoSuggestedAdditionalFields = (
  product: TestDriveProduct,
): TestDriveProduct => {
  if (!product.suggest_additional_fields) {
    return product
  }

  return {
    ...product,
    engine_type: null,
    engine_power_hp: null,
    engine_volume_cc: null,
    manufacture_date: null,
    excise_rate: undefined,
    excise_value: undefined,
  }
}

const getDemoScenarioExtraServices = (
  companies: TestDriveUserExtraService[],
): DemoExtraService[] => companies.map(company => ({
  ...company,
  cost_total: company.cost_total,
  currency: company.currency || 'USD',
  services: company.services.map(service => ({
    id: service.id,
    name: service.name,
    price: Number(service.price || 0),
    currency: company.currency || 'USD',
    is_demo_scenario_service: true,
  })),
}))

const getDemoFlatExtraServices = (
  services: TestDriveAdditionalService[],
): DemoExtraService[] => services.map(service => ({
  id: service.id,
  name: service.name,
  name_en: service.name,
  cost_total: Number(service.cost || 0),
  currency: service.currency || 'USD',
  services: [
    {
      id: service.id,
      name: service.name,
      price: Number(service.cost || 0),
      currency: service.currency || 'USD',
      price_description: service.description,
      is_demo_scenario_service: true,
    },
  ],
}))

export const getSelectedDemoExtraServices = (serviceIds: number[]): DemoExtraService[] => (
  demoExtraServicesCatalog.reduce<DemoExtraService[]>((companies, company) => {
    const services = company.services.filter(service => serviceIds.includes(service.id))

    if (!services.length) {
      return companies
    }

    companies.push({
      ...company,
      cost_total: services.reduce((total, service) => total + service.price, 0),
      services,
    })

    return companies
  }, [])
)

export const updateDemoExtraServices = (userExtraServices: DemoExtraService[]) => {
  if (!scenarioDetails) {
    return
  }

  scenarioDetails = {
    ...scenarioDetails,
    order: {
      ...scenarioDetails.order,
      userExtraServices,
    },
  }
}

export const getDemoExtraServicesSearchResponse = () => {
  const prices = demoExtraServicesCatalog.flatMap(company => (
    company.services.map(service => service.price)
  ))

  let maxPrice = 0
  let minPrice = 0

  if (prices.length) {
    maxPrice = Math.max(...prices)
    minPrice = Math.min(...prices)
  }

  return {
    count: demoExtraServicesCatalog.length,
    meta: {
      max_price: maxPrice,
      min_price: minPrice,
    },
    results: demoExtraServicesCatalog,
  }
}

export const hasDemoScenarioExtraServices = () => Boolean(demoExtraServicesCatalog.length)

export const normalizeDemoScenarioDetails = (
  details: TestDriveScenarioDetails,
): TestDriveScenarioDetails => {
  const sourceDeliveryType = details.order.summaryOrder.delivery_type
  const isContainerDelivery = sourceDeliveryType === 2

  const containerCounts = {
    ...DEMO_CONTAINER_COUNTS,
    ...(isContainerDelivery ? details.order.summaryOrder.containers : {}),
  }

  demoExtraServicesCatalog = getDemoFlatExtraServices(details.order.additionalServices || [])

  if (details.order.userExtraServices?.length) {
    demoExtraServicesCatalog = getDemoScenarioExtraServices(details.order.userExtraServices)
  }

  demoBankCommissionsCatalog = details.order.bankCommissions

  const bankCommissions = {
    ...details.order.bankCommissions,
    invoices: details.order.bankCommissions.invoices.map(invoice => ({
      ...invoice,
      banking_commissions: [],
    })),
  }

  const invoices = details.order.invoices.map(invoice => ({
    ...invoice,
    products: invoice.products?.map(resetDemoSuggestedAdditionalFields),
  }))

  return {
    ...details,
    order: {
      ...details.order,
      bankCommissions,
      invoices,
      summaryCompleted: {
        ...details.order.summaryCompleted,
        additional_services: false,
      },
      summaryOrder: {
        ...details.order.summaryOrder,
        containers: containerCounts,
        containers_user: containerCounts,
        delivery_type: sourceDeliveryType,
      },
      userExtraServices: [],
    },
  }
}

export const setDemoScenarioDetails = (details?: TestDriveScenarioDetails) => {
  scenarioDetails = details

  if (!details) {
    demoBankCommissionsCatalog = undefined
  }
}

export const getDemoBankCommissionsByInvoiceNumber = (invoiceNumber: string) => (
  demoBankCommissionsCatalog?.invoices.find(invoice => invoice.invoice_num === invoiceNumber)
)

export const getUrl = (args: any) => {
  const url = typeof args === 'string' ? args : args?.url

  return typeof url === 'string' ? url.replace(API_URL, '') : url
}

export const getMethod = (args: any) => typeof args === 'string' ? 'GET' : args?.method || 'GET'

export const getBody = (args: any) => typeof args === 'string' ? undefined : args?.body

export const getParams = (args: any) => typeof args === 'string' ? undefined : args?.params

export const getProductsByInvoiceId = (order: TestDriveOrder, invoiceId: number) =>
  order.invoices.find(invoice => invoice.id === invoiceId)?.products || []

export const getScenarioDetails = (state: any) => {
  const firstScenarioSlug = testDriveApi.endpoints.getTestDriveScenarios.select()(state)?.data?.[0]?.slug

  if (scenarioDetails) {
    return scenarioDetails
  }

  return firstScenarioSlug
    ? testDriveApi.endpoints.getTestDriveScenario.select(firstScenarioSlug)(state)?.data
    : undefined
}

export const getDictionaryResponse = (results: any[] = []) => ({
  results,
  count: results.length,
})

export const getDemoIncotermsResponse = (order?: TestDriveOrder) => {
  const scenarioIncoterms = order?.invoices.map(invoice => invoice.incoterms).filter(Boolean) || []
  const incoterms = Array.from(new Set([ ...scenarioIncoterms, 'exw', 'fca', 'fob' ]))

  return getDictionaryResponse(incoterms.map(incoterm => ({
    code: incoterm,
    name: incoterm.toUpperCase(),
  })))
}

export const getOrderSummaryResponse = getTestDriveOrderSummaryResponse

export type DemoPlacesCalculation = {
  pieces?: number
  pieces_in_cartoon?: number
  places?: number
  pieces_total?: number
  pieces_in_cartoon_total?: number
  places_total?: number
}

export const getDemoPlacesCalculation = (body?: DemoPlacesCalculation) => {
  const values = body || {}
  const pieces = Number(values.pieces || 0)
  const piecesInCartoon = Number(values.pieces_in_cartoon || 0)
  const places = Number(values.places || 0)
  const result = { ...values }

  if (pieces && piecesInCartoon) {
    result.places_total = pieces / piecesInCartoon
  }

  if (pieces && places) {
    result.pieces_in_cartoon_total = pieces / places
  }

  if (piecesInCartoon && places) {
    result.pieces_total = piecesInCartoon * places
  }

  return result
}
